import React, { useEffect } from 'react';
import { getFeatureFlagValue } from '@citrite/feature-flags';
import { AccountsEntity } from '@citrite/sf-api';
import { ProgressShareFileLogoDark } from '@sharefiledev/branding-assets';
import { SharefileTextLogoIcon } from '@sharefiledev/flow-web';
import { Branding, BrandingConstants } from '../../../constants';
import { FeatureFlag } from '../../../FeatureFlags';
import { decideLogoBasedOnPriority } from '../../../util/branding';
import { useAppStore } from '../../store';

interface BrandingLogoProps {
	width?: number;
}

export const BrandingLogo: React.FC<BrandingLogoProps> = ({ width }) => {
	const [rsLogoUrl, sfLogoUrl, setSfLogoUrl] = useAppStore(state => [
		state.rsLogoUrl,
		state.sfLogoUrl,
		state.setSfLogoUrl,
	]);
	const [isLoadingLogo, setIsLoadingLogo] = React.useState(false);
	const [logoDimensions, setLogoDimensions] = React.useState<{
		maxWidth: number;
		maxHeight: number;
	} | null>(null);

	useEffect(() => {
		const fetchSfBranding = async () => {
			setIsLoadingLogo(true);

			const brandingResponse = await AccountsEntity.getBranding().execute();
			const sfAccountLogoUrl = brandingResponse?.BrandingLinks?.LogoUrl;
			setSfLogoUrl(sfAccountLogoUrl ?? Branding.NoLogoSet);

			setIsLoadingLogo(false);
		};

		if (!rsLogoUrl && !sfLogoUrl) {
			fetchSfBranding();
		}
	}, [sfLogoUrl, rsLogoUrl, setSfLogoUrl]);

	const customLogoUrl = decideLogoBasedOnPriority(rsLogoUrl, sfLogoUrl);

	const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
		const img = event.currentTarget;
		let imgHeight = img.height;
		let imgWidth = img.width;

		if (imgHeight > BrandingConstants.MaxLogoHeight) {
			imgHeight = BrandingConstants.MaxLogoHeight;
			imgWidth = Math.min(
				(BrandingConstants.MaxLogoHeight / img.height) * img.width,
				BrandingConstants.MaxLogoWidth
			);
		} else if (imgWidth > BrandingConstants.MaxLogoWidth) {
			imgWidth = BrandingConstants.MaxLogoWidth;
			imgHeight = Math.min(
				(BrandingConstants.MaxLogoWidth / img.width) * img.height,
				BrandingConstants.MaxLogoHeight
			);
		}

		setLogoDimensions({ maxWidth: imgWidth, maxHeight: imgHeight });
	};

	return (
		<>
			{!isLoadingLogo && (
				<>
					{customLogoUrl ? (
						<img
							src={customLogoUrl || ''}
							alt="Branding Logo"
							style={{
								maxWidth: logoDimensions?.maxWidth,
								maxHeight: logoDimensions?.maxHeight,
							}}
							onLoad={handleImageLoad}
						/>
					) : getFeatureFlagValue(FeatureFlag.UseProgressShareFileBranding) ? (
						<ProgressShareFileLogoDark
							width={width + BrandingConstants.ProgressBrandingRelativeIncrement}
						/>
					) : (
						<SharefileTextLogoIcon width={width} />
					)}
				</>
			)}
		</>
	);
};

export default BrandingLogo;
